import { Link } from "@tanstack/react-router";
import { useLang } from "@/hooks/use-language";
import type { ReactNode } from "react";

interface Props {
  titleEn: string;
  titleAr: string;
  updatedEn?: string;
  updatedAr?: string;
  children: ReactNode;
}

/**
 * Shared shell for static legal/support pages.
 * Renders the correct direction, font stack, and Home link based on the
 * current global language (synced with the landing toggle).
 */
export function LegalShell({ titleEn, titleAr, updatedEn, updatedAr, children }: Props) {
  const { lang, dir, toggle } = useLang();
  const isAr = lang === "ar";

  return (
    <div dir={dir} className="min-h-dvh bg-background text-foreground">
      <header className="border-b border-clay/30 px-5 py-4">
        <div className="mx-auto flex max-w-3xl items-center justify-between gap-3">
          <Link
            to="/"
            className="text-sm font-semibold text-foreground/70 hover:text-primary"
          >
            {isAr ? "← الرئيسية" : "← Home"}
          </Link>
          <button
            type="button"
            onClick={toggle}
            aria-label={isAr ? "تبديل اللغة" : "Toggle language"}
            className="rounded-full border border-clay/50 bg-card px-3 py-1.5 text-xs font-bold tracking-widest text-foreground/70 hover:text-primary"
          >
            {isAr ? "EN" : "AR"}
          </button>
        </div>
      </header>

      <article
        className={`prose prose-neutral dark:prose-invert mx-auto max-w-3xl px-6 py-10 ${
          isAr ? "prose-headings:font-display-ar text-right" : ""
        }`}
        style={isAr ? { fontFamily: "'Cairo', system-ui, sans-serif" } : undefined}
      >
        <h1>{isAr ? titleAr : titleEn}</h1>
        {(updatedEn || updatedAr) && (
          <p className="text-sm text-muted-foreground">
            {isAr ? `آخر تحديث: ${updatedAr}` : `Last updated: ${updatedEn}`}
          </p>
        )}
        {children}
      </article>
    </div>
  );
}
